import { reactive } from 'vue';
import { holdVueDialogs, legacyDialogs, runWithVueDialogs } from './legacyDialogs';
import { log } from '@/utils/core/vueLogger';
import { LOGIN_PANELS, loginCode, visibleLoginPanel } from '@/utils/domain/loginModel';

// The Vue login page (src/components/login/LoginPage.vue) over the legacy login page (app/pages/login/login.js).
// That page keeps running hidden in the iframe: the SSO / LoginAs / delegation cookies, the offline login
// against the stored password hash, the automatic re-login (option NbDaysAllowedForLogin), the session it
// writes (ctx.user, USER / .0.USER / CUSTOMER, externis.ctx) and the hand-over to the portal all stay legacy.
// Vue fills the legacy form fields and presses the legacy buttons: login.js reads those fields itself (the
// password hash, remember me, the expired password form reuses the login fields), so they must hold what the
// user typed (md/ROUTER_MIGRATION.md "Phase 2c").

const POLL_MS = 500;
// Covers the legacy round trip (logon, customer, user infos) when no dialog reports the outcome.
const BUSY_TIMEOUT_MS = 20000;
// Main.js slideIn / slideOut: 400ms fade out, the next panel fades in 500ms later.
const PANEL_SWITCH_MS = 1200;

const initialState = () => ({ ready: false, panel: 'main', ssoCustomer: '', prefill: { userName: '', customerCode: '', remember: false }, busy: false });

export const legacyLogin = reactive(initialState());

let win = null;
let active = false;
let pollTimer = null;
let busyTimer = null;
let busyToasts = 0;
let panelLockedUntil = 0;
let releaseDialogs = null;

const safe = (fn, fallback = null) => {
	try {
		const value = fn();
		return value === undefined ? fallback : value;
	} catch {
		return fallback;
	}
};

const vmLogin = () => safe(() => win.externis.vmLogin);
const field = (id) => safe(() => win.document.getElementById(id));
const isShown = (el) => Boolean(el) && safe(() => win.getComputedStyle(el).display, 'none') !== 'none';

function assign(key, value) {
	if (JSON.stringify(legacyLogin[key]) !== JSON.stringify(value)) legacyLogin[key] = value;
}

function setBusy(busy) {
	clearTimeout(busyTimer);
	busyTimer = null;
	legacyLogin.busy = busy;
	if (!busy) return;
	busyToasts = legacyDialogs.toasts.length;
	busyTimer = setTimeout(() => setBusy(false), BUSY_TIMEOUT_MS);
}

export function refreshLegacyLogin() {
	const vm = vmLogin();
	const doc = safe(() => win.document);
	assign('ready', Boolean(vm));
	if (!vm || !doc) return;

	assign('ssoCustomer', safe(() => vm.ssoCustomer(), '') || '');
	// login.js setSavedCredentials() fills the legacy fields from the remembered user.
	assign('prefill', { userName: safe(() => field('userNameTextField').value, '') || '', customerCode: safe(() => field('customerCodeTextField').value, '') || '', remember: Boolean(safe(() => field('rememberMeCheckbox').checked, false)) });

	if (Date.now() >= panelLockedUntil) {
		const visibility = Object.fromEntries(LOGIN_PANELS.map(({ name, selector }) => [name, isShown(safe(() => doc.querySelector(selector)))]));
		const panel = visibleLoginPanel(visibility);
		if (panel && panel !== legacyLogin.panel) {
			legacyLogin.panel = panel;
			setBusy(false);
		}
	}

	// A legacy dialog or message reports the outcome (errors, ERR-xxxx, mail sent…).
	if (legacyLogin.busy && (legacyDialogs.current || legacyDialogs.toasts.length > busyToasts)) setBusy(false);
}

function fill(values) {
	Object.entries(values).forEach(([id, value]) => {
		const input = field(id);
		if (input) input.value = value ?? '';
	});
}

function press(label, handler, { busy = true } = {}) {
	const vm = vmLogin();
	if (!vm) return false;
	if (busy) setBusy(true);
	try {
		runWithVueDialogs(win, () => handler(vm));
		return true;
	} catch (error) {
		setBusy(false);
		log('error', 'legacyLogin', `${label} failed: ${error.message}`);
		return false;
	}
}

// Vue-driven panel switches show the target at once; the legacy fade then confirms it.
function showPanel(panel) {
	panelLockedUntil = Date.now() + PANEL_SWITCH_MS;
	legacyLogin.panel = panel;
}

// login.js loginHandler(): reads the three fields; __successLogin() hashes passwordTextField, launchApp()
// reads #rememberMeCheckbox. username / userpass / customerCode are never bound in login.htm, so login.js
// lost the customer login code and checked an offline login against an empty password: they are set too.
export function submitLegacyLogin({ userName, password, customerCode, remember } = {}) {
	const user = loginCode(userName);
	const customer = loginCode(customerCode);
	return press('login', (vm) => {
		fill({ userNameTextField: user, passwordTextField: password, customerCodeTextField: customer });
		const checkbox = field('rememberMeCheckbox');
		if (checkbox) checkbox.checked = Boolean(remember);
		safe(() => {
			vm.username(user);
			vm.userpass(password ?? '');
			vm.customerCode(customer);
		});
		vm.loginHandler();
	});
}

export const ssoLegacyLogin = () => press('sso', (vm) => vm.ssoCustomerHandler());

export function showLegacyForgottenPassword({ customerCode } = {}) {
	if (!press('forgotten', (vm) => vm.toggleFormsReset(), { busy: false })) return false;
	fill({ passwordForgottenCustomerCodeTextField: loginCode(customerCode) });
	showPanel('forgotten');
	return true;
}

export function backToLegacyLogin() {
	if (!press('back', (vm) => vm.waiterResetEnd(), { busy: false })) return false;
	showPanel('main');
	return true;
}

// login.js resetHandler(): browser validity of its email and customer fields, then Auth/ResetPass.
export const requestLegacyPasswordReset = ({ email, customer } = {}) =>
	press('resetPassword', (vm) => {
		fill({ passwordForgottenEmailTextField: String(email ?? '').trim(), passwordForgottenCustomerCodeTextField: loginCode(customer) });
		vm.resetHandler();
	});

// login.js changePassHandler(): inputs, confirmation and the password complexity, then Auth/ChangePass.
export const changeLegacyPassword = ({ email, customer, key, password, confirm } = {}) =>
	press('changePassword', (vm) => {
		fill({ resetEmailTextField: String(email ?? '').trim(), resetCustomerCodeTextField: loginCode(customer), resetKeyTextField: key, resetPasswordTextField: password, resetConfirmPasswordTextField: confirm });
		safe(() => {
			vm.newUserpass(password ?? '');
			vm.newUserpassConfirm(confirm ?? '');
		});
		vm.changePassHandler();
	});

// login.js updatePassHandler(): the user name and customer of the login form, then Auth/SetPass.
export const updateLegacyExpiredPassword = ({ oldPassword, newPassword } = {}) =>
	press('updatePassword', (vm) => {
		fill({ passwordExpiredTextField: oldPassword, passwordConfirmExpiredTextField: newPassword });
		vm.updatePassHandler();
	});

// login.js validLanguageSwitch(): read by Globalization.js and src/i18n/bootstrap.js on the next load.
export function changeLoginLanguage(locale) {
	try {
		localStorage.setItem('loginLocale', locale);
	} catch {
		return;
	}
	window.location.reload();
}

function start() {
	if (!win) return;
	releaseDialogs ??= holdVueDialogs(win);
	refreshLegacyLogin();
	pollTimer ??= setInterval(refreshLegacyLogin, POLL_MS);
}

function stop() {
	clearInterval(pollTimer);
	pollTimer = null;
	releaseDialogs?.();
	releaseDialogs = null;
	setBusy(false);
}

// iframeWatcher: whether the legacy currently shows its login page.
export function setLegacyLoginActive(isActive) {
	active = Boolean(isActive);
	if (active) start();
	else stop();
}

// Called by iframeWatcher once amplify and externis exist in a freshly loaded legacy document.
export function attachLegacyLogin(iframeWindow) {
	if (!iframeWindow || win === iframeWindow) return;
	if (win) detachLegacyLogin();
	win = iframeWindow;
	if (active) start();
}

// Called on every iframe load, before anything reads the new document.
export function detachLegacyLogin() {
	stop();
	win = null;
	active = false;
	panelLockedUntil = 0;
	Object.assign(legacyLogin, initialState());
}
